import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { StackedSidebarService } from './stacked-sidebar.service';
import { SidebarType } from '../components/layout/stacked-sidebar/stacked-sidebar.interface';
import type { ContactFormResult } from '../components/board-v3/contacts/contact-form/contact-form.component';
import type { LinkContactResult } from '../components/board-v3/contacts/link-contact/link-contact.component';
import type { ContactDetailResult } from '../components/board-v3/contacts/contact-detail/contact-detail.component';
import {
  BoardContact,
  BoardContactsPage,
  BoardFilterSet,
  CreateContactPayload,
  GetContactsParams,
  SaveBoardFilterPayload,
  UpdateContactPayload,
} from '../components/board-v3/board-v3.types';

@Injectable({
  providedIn: 'root',
})
export class BoardContactService {
  private readonly http = inject(HttpClient);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly url = environment.baseURL + '/board/contact';

  public getContacts(params: GetContactsParams = {}): Observable<BoardContactsPage> {
    const query: Record<string, string | number> = {};
    if (params.search?.trim()) query['search'] = params.search.trim();
    if (params.page) query['page'] = params.page;
    if (params.take) query['take'] = params.take;
    if (params.filters?.length) query['filters'] = JSON.stringify(params.filters);
    if (params.filter_mode) query['filter_mode'] = params.filter_mode;

    return this.http
      .get<{ data: BoardContactsPage }>(this.url, { params: query })
      .pipe(map((res) => res.data ?? { items: [], total: 0, size: 0, page: 1 }));
  }

  public getContact(contactId: string): Observable<BoardContact> {
    return this.http.get<{ data: BoardContact }>(`${this.url}/${contactId}`).pipe(map((res) => res.data));
  }

  public getFilters(): Observable<BoardFilterSet[]> {
    return this.http.get<{ data: BoardFilterSet[] }>(`${this.url}/filters`).pipe(map(res => res.data));
  }

  public createFilter(payload: SaveBoardFilterPayload): Observable<BoardFilterSet> {
    return this.http.post<{ data: BoardFilterSet }>(`${this.url}/filters`, payload).pipe(map(res => res.data));
  }

  public updateFilter(id: string, payload: SaveBoardFilterPayload): Observable<BoardFilterSet> {
    return this.http.put<{ data: BoardFilterSet }>(`${this.url}/filters/${id}`, payload).pipe(map(res => res.data));
  }

  public deleteFilter(id: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/filters/${id}`);
  }

  public createContact(payload: CreateContactPayload): Observable<BoardContact> {
    return this.http.post<{ data: BoardContact }>(this.url, payload).pipe(map((res) => res.data));
  }

  /** Only the sent fields change; `phones` / `emails` replace the whole list */
  public updateContact(contactId: string, payload: UpdateContactPayload): Observable<BoardContact> {
    return this.http.put<{ data: BoardContact }>(`${this.url}/${contactId}`, payload).pipe(map((res) => res.data));
  }

  public deleteContact(contactId: string): Observable<void> {
    return this.http.delete(`${this.url}/${contactId}`).pipe(map(() => undefined));
  }

  /** Already linked cards are skipped */
  public linkCards(contactId: string, cardIds: string[]): Observable<BoardContact> {
    return this.http
      .post<{ data: BoardContact }>(`${this.url}/${contactId}/card`, { card_ids: cardIds })
      .pipe(map((res) => res.data));
  }

  public unlinkCard(contactId: string, cardId: string): Observable<BoardContact> {
    return this.http
      .delete<{ data: BoardContact }>(`${this.url}/${contactId}/card/${cardId}`)
      .pipe(map((res) => res.data));
  }

  // #region sidebars

  /** Creates a contact, linked to `cardIds` right away when given */
  public openContactForm(cardIds: string[] = []): Observable<ContactFormResult | undefined> {
    return this.stackedSidebarService.openSidebar(
      { type: SidebarType.BoardV3ContactForm, width: '440px' },
      { cardIds },
    );
  }

  public openLinkContact(excludes: string[] = []): Observable<LinkContactResult | undefined> {
    return this.stackedSidebarService.openSidebar(
      { type: SidebarType.BoardV3LinkContact, width: '440px' },
      { excludes },
    );
  }

  /**
   * `canOpenCards` must be off inside a card detail: a nested card detail would replace
   * the shared `BoardService.card` of the outer one
   */
  public openContactDetail(contactId: string, canOpenCards = true): Observable<ContactDetailResult | undefined> {
    return this.stackedSidebarService.openSidebar(
      { type: SidebarType.BoardV3ContactDetail, width: '90vw' },
      { contactId, canOpenCards },
    );
  }

  // #endregion
}
